import { CompanyExportParams } from "../pages/Company/components/CompanyExport/types";
import { ExportData } from "../pages/Workspace/ExportModal/types";
import { axiosInstance } from "../utils/axios";

export interface CreditBalanceResponse {
  credits: number;
  balance: number;
}

export type CreditsBreakdownEntry = {
  /** Rows matching the selection, ignoring export size caps */
  counts: number;
  /**
   * Rows the export will actually contain once the size caps are applied, which is
   * what `credits` is billed on. Absent on responses from an older API version.
   */
  export_counts?: number;
  credits: number;
};

export type EstimateCreditsDetailResponse = {
  credits_estimate: number;
  details: Record<string, CreditsBreakdownEntry>;
};

/**
 * Shape returned by CompanyExportModal's Universe-mode payload builder
 * (`{ params: {...}, scope }`) — distinct from the classic ExportData/
 * CompanyExportParams shapes but accepted by the same estimate endpoint.
 */
export type UniverseExportPayload = {
  params: Record<string, unknown>;
  scope: string;
};

export type EstimateExportCreditsRequest = {
  /**
   * The export data for which to estimate credits
   */
  export_payload: ExportData | CompanyExportParams | UniverseExportPayload;
};

const API_URL = "/export-tracking-api/api";

/**
 * Fetches the credit balance information for the current user
 * @returns Promise with the balance and credits information
 */
export const fetchCreditBalance = async (): Promise<CreditBalanceResponse> => {
  const response = await axiosInstance.get<CreditBalanceResponse>(
    `${API_URL}/balance`
  );
  return response.data;
};

/**
 * Estimates the export credits for the current user, including a per-category
 * breakdown matching OppGen's sales_quote endpoint.
 */
export const estimateExportCredits = async (
  payload: EstimateExportCreditsRequest
): Promise<EstimateCreditsDetailResponse> => {
  const response = await axiosInstance.post<EstimateCreditsDetailResponse>(
    `${API_URL}/exports/estimate/sales_quote`,
    payload
  );

  return response.data;
};
